import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DATE_WITH_TIME_FORMAT } from '@utils/constants';
import dayjs from 'dayjs';
import i18n from 'i18next';

import { offerName } from '../constants';
import { offerConstants, offerModels } from '..';

import { OfferCreateOrUpdateStateType, OfferStoreType } from './types';

const initialState: OfferStoreType = {
  table: {
    totalPages: 0,
    currentPage: 1,
    totalElements: 0
  },
  trash: {
    visible: false
  },
  view: {
    page: 1,
    currentPage: 1,
    visible: false,
    type: 'ACTIVE',
    totalElements: 0
  },
  createOrUpdate: offerConstants.initialCreateOrUpdateOfferState
};



const offerSlice = createSlice({
  name: 'offer-slice',
  initialState,
  reducers: {
    // TRASH
    setTrash: (state, actions: PayloadAction<OfferStoreType['trash']>) => {
      state.trash = actions.payload;
    },

    // CREATE OR UPDATE
    setCreateOrUpdate: (state, actions: PayloadAction<OfferStoreType['createOrUpdate']>) => {
      state.createOrUpdate = actions.payload;
    },
    setCreateOrUpdateVisible: (state, actions: PayloadAction<{ id?: number; visible: boolean }>) => {
      state.createOrUpdate.id = actions.payload?.id;
      state.createOrUpdate.visible = actions.payload?.visible;
      state.createOrUpdate.name = `${offerName[i18n.language]} ${dayjs().format(DATE_WITH_TIME_FORMAT)}`;
    },
    setCreateOrUpdateItem: <TKey extends keyof OfferStoreType['createOrUpdate']>(
      state: OfferStoreType,
      actions: PayloadAction<{
        key: TKey;
        value: OfferStoreType['createOrUpdate'][TKey];
      }>
    ) => {
      state.createOrUpdate[actions.payload.key] = actions.payload.value;
    },
    setCreateOrUpdateFilterItem: <TKey extends keyof OfferCreateOrUpdateStateType['filter']>(
      state: OfferStoreType,
      actions: PayloadAction<{
        key: TKey;
        value: OfferCreateOrUpdateStateType['filter'][TKey];
      }>
    ) => {
      state.createOrUpdate.filter[actions.payload.key] = actions.payload.value;
    },
    setCreateOrUpdateDeselectHouse: (state, action: PayloadAction<number>) => {
      state.createOrUpdate.filter.buildings = state.createOrUpdate.filter.buildings?.filter(
        i => JSON.parse(i)?.houseId !== action.payload
      );

      state.createOrUpdate.flatIds = [];
    },
    setCreateOrUpdateDeselectBuilding: state => {
      state.createOrUpdate.flatIds = [];
    },
    setCreateOrUpdateIsDirtyHouseOrBuilding: state => {
      state.createOrUpdate.isDirtyHouseOrBuilding = true;
    },
    setCreateOrUpdateAllChecked: (state, actions: PayloadAction<boolean>) => {
      if (actions.payload) {
        state.createOrUpdate.ignoreFlatIds = [];
        state.createOrUpdate.flatIds = [];
      }

      state.createOrUpdate.allChecked = actions.payload;
    },
    setCreateOrUpdateTotalElements: (state, actions: PayloadAction<number | undefined>) => {
      state.createOrUpdate.totalElements = actions.payload;
    },
    setCreateOrUpdateClearFlats: state => {
      state.createOrUpdate.ignoreFlatIds = [];
      state.createOrUpdate.flatIds = [];
      state.createOrUpdate.allChecked = false;
    },

    // VIEW
    setView: (state, actions: PayloadAction<OfferStoreType['view']>) => {
      state.view = actions.payload;
    },
    setViewPage: (state, actions: PayloadAction<number>) => {
      state.view.page = actions.payload;
    },
    setViewType: (state, actions: PayloadAction<offerModels.OfferStatusType>) => {
      state.view.type = actions.payload;
    },
    setViewTotalItems: (state, actions: PayloadAction<{ totalElements: number; currentPage: number }>) => {
      state.view.totalElements = actions.payload.totalElements;
      state.view.currentPage = actions.payload.currentPage;
    },

    // TABLE
    setTableItems: (state, actions: PayloadAction<OfferStoreType['table']>) => {
      state.table = actions.payload;
    }
  }
});

export const offerReducer = offerSlice.reducer;
export const {
  setCreateOrUpdate,
  setCreateOrUpdateItem,
  setTrash,
  setCreateOrUpdateFilterItem,
  setCreateOrUpdateVisible,
  setCreateOrUpdateIsDirtyHouseOrBuilding,
  setCreateOrUpdateDeselectHouse,
  setCreateOrUpdateDeselectBuilding,
  setView,
  setViewPage,
  setCreateOrUpdateAllChecked,
  setCreateOrUpdateTotalElements,
  setCreateOrUpdateClearFlats,
  setTableItems,
  setViewTotalItems,
  setViewType
} = offerSlice.actions;
